/**
 * Print / PDF gate for the public shareable pages (`/shareable/estimate`,
 * `/shareable/depositInvoice`, `/shareable/invoice`).
 *
 * Those pages render without a login (the `*ForPage` endpoints are public),
 * but the Print button goes through the pdf-service, which calls the backend
 * `*ForPdf` endpoints with the viewer's JWT. Signed out, the backend answers
 * 401 "Token is missing" and the viewer only saw a raw "Error" dialog
 * (Todd Gallagher, Metro AV, 2026-09-14: "Your print button is not working").
 *
 * Instead of hiding the button, the page keeps it visible and explains WHY the
 * PDF cannot be generated, with a way to sign in and come back to the link.
 * Pure helpers here; the React side lives in `customHooks/useShareablePrintGate`.
 */
export type ShareableDocumentKind = "estimate" | "depositInvoice" | "invoice";

const DOCUMENT_LABELS: Record<ShareableDocumentKind, string> = {
  estimate: "estimate",
  depositInvoice: "deposit invoice",
  invoice: "invoice",
};

export const SUPPORT_EMAIL = "support@showphaze.com";

export interface ShareablePrintBlockReason {
  title: string;
  message: string;
}

export const getShareableDocumentLabel = (kind?: ShareableDocumentKind): string =>
  (kind && DOCUMENT_LABELS[kind]) || "document";

/**
 * Null when the PDF can be requested; otherwise the dialog the page shows
 * instead of calling the pdf-service. Today the only blocker is being signed
 * out; keep new reasons here so the three pages stay in sync.
 */
export const getShareablePrintBlockReason = ({
  isAuthenticated,
  kind,
}: {
  isAuthenticated: boolean;
  kind?: ShareableDocumentKind;
}): ShareablePrintBlockReason | null => {
  if (isAuthenticated) return null;
  const label = getShareableDocumentLabel(kind);
  return {
    title: "Sign in to download the PDF",
    message:
      `The ${label} PDF can't be generated because you're not signed in. ` +
      `You can review the ${label} on this page, but the PDF is generated by your ShowPhaze account.\n\n` +
      `Sign in and you'll be brought right back to this page. ` +
      `If you don't have a ShowPhaze login, contact your ShowPhaze coordinator or ${SUPPORT_EMAIL} and we'll send you the PDF.`,
  };
};

/**
 * Friendly text for a pdf-service failure. A 401 from the pdf-service is
 * always the backend refusing the forwarded token (missing or expired), so
 * say that instead of surfacing "Token is missing".
 */
export const describePdfRequestError = ({
  status,
  fallback,
}: {
  status?: number;
  fallback?: string;
}): string => {
  if (status === 401) {
    return (
      "Your session has expired or you're not signed in, so the PDF can't be generated. " +
      "Please sign in again and try once more."
    );
  }
  return fallback || "Failed to generate the PDF";
};
